import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import InputLabel from 'components/InputLabel/index';
import SectionCard from 'components/section/SectionCard';
import { filterOptionLabel } from 'pages/globals/global';
import constantFormValues from '../utils/formInputNames';
import { Col, DatePicker, Form, Row, Select } from 'antd';
import { Fragment, useEffect, useRef, useMemo } from 'react';
import CardAccordion from 'components/CardAccordion/CardAccordion';
import { useGetQueryV3DeltaQuery } from 'store/features/api/graphSlice';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { glCodesShortForShow, glCodesToShow } from 'static-solutions/24155/identity-solution/general-ledger-code/GLUtility';

const LeaseRecordManagementV9AccountAssignmentDetail = ({ identityId, leaseData, form }) => {
	const { state } = useLocation();

	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [getAccountingAssignment] = useSendControlDataMutation();
	const { data: generalLedgerRes, isLoading: isGeneralLedgerListLoading } = useGetQueryV3DeltaQuery(
		{
			companyCode,
			fieldCodes: ['companyCode'],
			fieldValues: [companyCode],
			name: 'queryGLPostingAccountDynamic',
		},
		{ skipQuery: companyCode ? true : false }
	);

	const glOptions = useMemo(() => {
		return (
			generalLedgerRes?.results?.map((item) => {
				return { label: item?.displayName, value: item.propertyValue };
			}) || []
		);
	}, [generalLedgerRes]);

	const lastPayloadRef = useRef(null);

	function formatAccountingAssignmentData(inputData) {
		const data = typeof inputData === 'string' ? JSON.parse(inputData) : inputData;
		const formattedOutput = {};
		for (const key in data) {
			const entry = data[key];
			if (entry === null) continue;

			if (key === 'securityDeposit') {
				formattedOutput['securityDepositName'] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			} else {
				formattedOutput[key] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
			}
		}
		return formattedOutput;
	}

	const setAccountingAssignmentData = async (payload) => {
		if (!payload || payload.data == null) {
			return;
		}

		if (JSON.stringify(lastPayloadRef.current) === JSON.stringify(payload)) {
			return;
		}

		lastPayloadRef.current = payload;

		await getAccountingAssignment(payload).then((res) => {
			form.setFieldsValue({
				...formatAccountingAssignmentData(res?.data?.responseData),
			});
		});
	};

	useEffect(() => {
		if (identityId) {
			const payload = {
				data: identityId,
				processCode: state?.processCode,
				scriptName: 'lease-controller-accounts',
			};

			setAccountingAssignmentData(payload);
		}
	}, [identityId]);

	useEffect(() => {
		if (leaseData?.data) {
			form.setFieldsValue({
				gl2_rouAssets: leaseData?.data?.gl2_rouAssets,
				gl2_leaseLiabilityST: leaseData?.data?.gl2_leaseLiabilityST,
				gl2_leaseLiabilityLT: leaseData?.data?.gl2_leaseLiabilityLT,
				gl2_interestExpense: leaseData?.data?.gl2_interestExpense,
				gl2_leasePayables: leaseData?.data?.gl2_leasePayables,
				gl2_securityDeposit: leaseData?.data?.gl2_securityDeposit,
				gl2_depreciation: leaseData?.data?.gl2_depreciation,
				gl2_accumulatedDepreciation: leaseData?.data?.gl2_accumulatedDepreciation,
				gl2_gainFromLeases: leaseData?.data?.gl2_gainFromLeases,
				gl2_lossFromLeases: leaseData?.data?.gl2_lossFromLeases,

				gl_rouAssetInitialRecognition: leaseData?.data?.gl_rouAssetInitialRecognition,
				gl_rouAssetModification: leaseData?.data?.gl_rouAssetModification,
				gl_rouAssetPrepayments: leaseData?.data?.gl_rouAssetPrepayments,
				gl_rouAssetUpfrontPayment: leaseData?.data?.gl_rouAssetUpfrontPayment,
				gl_rouAssetIndirectCosts: leaseData?.data?.gl_rouAssetIndirectCosts,
				gl_rouCashIncentive: leaseData?.data?.gl_rouCashIncentive,
				gl_rouAssetKnockOff: leaseData?.data?.gl_rouAssetKnockOff,
				gl_securityDeposit: leaseData?.data?.gl_securityDeposit,
				gl_leaseLiabilityInitialRecognition: leaseData?.data?.gl_leaseLiabilityInitialRecognition,
				gl_leaseLiabilityModification: leaseData?.data?.gl_leaseLiabilityModification,
				gl_leaseLiabilityPayment: leaseData?.data?.gl_leaseLiabilityPayment,
				gl_leaseLiabilityInterest: leaseData?.data?.gl_leaseLiabilityInterest,
				gl_interestExpense: leaseData?.data?.gl_interestExpense,
				gl_depreciationRou: leaseData?.data?.gl_depreciationRou,
				gl_accumDepreciationRouDepreciation: leaseData?.data?.gl_accumDepreciationRouDepreciation,
				gl_accumDepreciationRouKnockOff: leaseData?.data?.gl_accumDepreciationRouKnockOff,
				gl_apControlLedgerPrepayments: leaseData?.data?.gl_apControlLedgerPrepayments,
				gl_apControlLedgerUpfrontPayment: leaseData?.data?.gl_apControlLedgerUpfrontPayment,
				gl_apControlLedgerIndirectCosts: leaseData?.data?.gl_apControlLedgerIndirectCosts,
				gl_apControlLedgerSecurityDeposit: leaseData?.data?.gl_apControlLedgerSecurityDeposit,
				gl_apControlLedgerPayment: leaseData?.data?.gl_apControlLedgerPayment,
				gl_arCashIncentive: leaseData?.data?.gl_arCashIncentive,
			});
		}
	}, [leaseData?.data]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<SectionCard
				sx={{
					padding: '16px',
					borderRadius: '10px',
					border: '1px solid #e6ebf2',
				}}
			>
				<Typography
					variant="h4"
					sx={{
						color: '#1a3b5d',
						padding: '0 0 12px 0',
						fontWeight: 600,
						fontSize: '18px',
						borderBottom: '1px solid #e0e6ed',
						marginBottom: '16px',
					}}
				>
					Reporting Information
				</Typography>
				<Row gutter={[16, 0]}>
					<Col xs={8}>
						<Form.Item
							initialValue={dayjs().month(11).date(31)}
							name={constantFormValues?.endOfFinancialYear?.key}
							label={constantFormValues?.endOfFinancialYear?.label}
							rules={[
								{
									required: false,
								},
							]}
						>
							<DatePicker
								disabled
								picker="date"
								format="DD MMMM"
								allowClear={false}
								disabledDate={() => false} style={{width: '100%'}}
								onChange={(date) => {
									if (date) {
										const currentYear = dayjs().year();
										const newDate = date.year(currentYear);
										form.setFieldsValue({
											[constantFormValues?.endOfFinancialYear?.key]: newDate,
										});
									}
								}}
							/>
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			<SectionCard
				sx={{
					padding: '16px',
					borderRadius: '10px',
					border: '1px solid #e6ebf2',
				}}
			>
				<Typography
					variant="h4"
					sx={{
						color: '#1a3b5d',
						padding: '0 0 12px 0',
						fontWeight: 600,
						fontSize: '18px',
						borderBottom: '1px solid #e0e6ed',
						marginBottom: '16px',
					}}
				>
					Accounting Information
				</Typography>

				<Row gutter={[16, 0]}>
					{Object.entries(glCodesShortForShow).map(([label, key], index) => (
						<Fragment key={index}>
							<Col xs={8}>
								<Form.Item name={key} label={label}>
									<Select
										disabled
										allowClear
										showSearch
										autoComplete="off"
										options={glOptions}
										filterOption={filterOptionLabel}
										placeholder="Select from the list"
										loading={isGeneralLedgerListLoading}
										styles={{
											popup: {
												root: { zIndex: 1301 },
											},
										}}
									/>
								</Form.Item>
							</Col>
						</Fragment>
					))}
				</Row>
			</SectionCard>

			<CardAccordion
				title="Accounting Information"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2, display: 'none' }}
				>
					<Row gutter={[16, 0]}>
					{Object.entries(glCodesToShow).map(([label, key], index) => (
						<Fragment key={index}>
							<Col xs={4}>
								<InputLabel label={label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={key}>
									<Select
										allowClear
										showSearch
										placeholder="Select from the list"
										autoComplete="off"
										styles={{
											popup: {
												root: { zIndex: 1301 },
											},
										}}
										filterOption={filterOptionLabel}
										options={glOptions}
										loading={isGeneralLedgerListLoading}
										disabled
									/>
								</Form.Item>
							</Col>
						</Fragment>
					))}
					</Row>
				</CardAccordion>
		</Box>
	);
};

export default LeaseRecordManagementV9AccountAssignmentDetail;

// import dayjs from 'dayjs';
// import { useSelector } from 'react-redux';
// import { useLocation } from 'react-router-dom';
// import InputLabel from 'components/InputLabel/index';
// import { filterOptionLabel } from 'pages/globals/global';
// import constantFormValues from '../utils/formInputNames';
// import { Col, DatePicker, Form, Row, Select } from 'antd';
// import { Fragment, useEffect, useRef, useMemo } from 'react';
// import CardAccordion from 'components/CardAccordion/CardAccordion';
// import { useGetQueryV3DeltaQuery } from 'store/features/api/graphSlice';
// import { useSendControlDataMutation } from 'store/features/api/apiSlice';
// import { glCodesShortForShow, glCodesToShow } from 'static-solutions/24155/identity-solution/general-ledger-code/GLUtility';

// const LeaseRecordManagementV9AccountAssignmentDetail = ({ identityId, leaseData, form }) => {
// 	const { state } = useLocation();

// 	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

// 	const [getAccountingAssignment] = useSendControlDataMutation();
// 	const { data: generalLedgerRes, isLoading: isGeneralLedgerListLoading } = useGetQueryV3DeltaQuery(
// 		{
// 			companyCode,
// 			fieldCodes: ['companyCode'],
// 			fieldValues: [companyCode],
// 			name: 'queryGLPostingAccountDynamic',
// 		},
// 		{ skipQuery: companyCode ? true : false }
// 	);

// 	const glOptions = useMemo(() => {
// 		return (
// 			generalLedgerRes?.results?.map((item) => {
// 				return { label: item?.displayName, value: item.propertyValue };
// 			}) || []
// 		);
// 	}, [generalLedgerRes]);

// 	const lastPayloadRef = useRef(null);

// 	function formatAccountingAssignmentData(inputData) {
// 		const data = typeof inputData === 'string' ? JSON.parse(inputData) : inputData;
// 		const formattedOutput = {};
// 		for (const key in data) {
// 			const entry = data[key];
// 			if (entry === null) continue;

// 			if (key === 'securityDeposit') {
// 				formattedOutput['securityDepositName'] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
// 			} else {
// 				formattedOutput[key] = `${entry.generalLedgerCode} - ${entry.generalLegerDescription}`;
// 			}
// 		}
// 		return formattedOutput;
// 	}
// 	const setAccountingAssignmentData = async (payload) => {
// 		if (!payload || payload.data == null) {
// 			return;
// 		}

// 		if (JSON.stringify(lastPayloadRef.current) === JSON.stringify(payload)) {
// 			return;
// 		}

// 		lastPayloadRef.current = payload;

// 		await getAccountingAssignment(payload).then((res) => {
// 			form.setFieldsValue({
// 				...formatAccountingAssignmentData(res?.data?.responseData),
// 			});
// 		});
// 	};
// 	useEffect(() => {
// 		if (identityId) {
// 			const payload = {
// 				data: identityId,
// 				processCode: state?.processCode,
// 				scriptName: 'lease-controller-accounts',
// 			};

// 			setAccountingAssignmentData(payload);
// 		}
// 	}, [identityId]);

// 	useEffect(() => {
// 		if (leaseData?.data) {
// 			form.setFieldsValue({
// 				gl2_rouAssets: leaseData?.data?.gl2_rouAssets,
// 				gl2_leaseLiabilityST: leaseData?.data?.gl2_leaseLiabilityST,
// 				gl2_leaseLiabilityLT: leaseData?.data?.gl2_leaseLiabilityLT,
// 				gl2_interestExpense: leaseData?.data?.gl2_interestExpense,
// 				gl2_leasePayables: leaseData?.data?.gl2_leasePayables,
// 				gl2_securityDeposit: leaseData?.data?.gl2_securityDeposit,
// 				gl2_depreciation: leaseData?.data?.gl2_depreciation,
// 				gl2_accumulatedDepreciation: leaseData?.data?.gl2_accumulatedDepreciation,
// 				gl2_gainFromLeases: leaseData?.data?.gl2_gainFromLeases,
// 				gl2_lossFromLeases: leaseData?.data?.gl2_lossFromLeases,

// 				gl_rouAssetInitialRecognition: leaseData?.data?.gl_rouAssetInitialRecognition,
// 				gl_rouAssetModification: leaseData?.data?.gl_rouAssetModification,
// 				gl_rouAssetPrepayments: leaseData?.data?.gl_rouAssetPrepayments,
// 				gl_rouAssetUpfrontPayment: leaseData?.data?.gl_rouAssetUpfrontPayment,
// 				gl_rouAssetIndirectCosts: leaseData?.data?.gl_rouAssetIndirectCosts,
// 				gl_rouCashIncentive: leaseData?.data?.gl_rouCashIncentive,
// 				gl_rouAssetKnockOff: leaseData?.data?.gl_rouAssetKnockOff,
// 				gl_securityDeposit: leaseData?.data?.gl_securityDeposit,
// 				gl_leaseLiabilityInitialRecognition: leaseData?.data?.gl_leaseLiabilityInitialRecognition,
// 				gl_leaseLiabilityModification: leaseData?.data?.gl_leaseLiabilityModification,
// 				gl_leaseLiabilityPayment: leaseData?.data?.gl_leaseLiabilityPayment,
// 				gl_leaseLiabilityInterest: leaseData?.data?.gl_leaseLiabilityInterest,
// 				gl_interestExpense: leaseData?.data?.gl_interestExpense,
// 				gl_depreciationRou: leaseData?.data?.gl_depreciationRou,
// 				gl_accumDepreciationRouDepreciation: leaseData?.data?.gl_accumDepreciationRouDepreciation,
// 				gl_accumDepreciationRouKnockOff: leaseData?.data?.gl_accumDepreciationRouKnockOff,
// 				gl_apControlLedgerPrepayments: leaseData?.data?.gl_apControlLedgerPrepayments,
// 				gl_apControlLedgerUpfrontPayment: leaseData?.data?.gl_apControlLedgerUpfrontPayment,
// 				gl_apControlLedgerIndirectCosts: leaseData?.data?.gl_apControlLedgerIndirectCosts,
// 				gl_apControlLedgerSecurityDeposit: leaseData?.data?.gl_apControlLedgerSecurityDeposit,
// 				gl_apControlLedgerPayment: leaseData?.data?.gl_apControlLedgerPayment,
// 				gl_arCashIncentive: leaseData?.data?.gl_arCashIncentive,
// 			});
// 		}
// 	}, [leaseData?.data]);

// 	return (
// 		<>
// 			<CardAccordion
// 				title="Reporting Information"
// 				customHeaderSX={{
// 					backgroundColor: '#e1f0ff !important',
// 					p: '10px',
// 				}}
// 				typographySX={{ fontSize: '16px !important' }}
// 				darkTitle={true}
// 				sx={{ mb: 2 }}
// 			>
// 				<Row gutter={[16, 0]}>
// 					<Col xs={4}>
// 						<InputLabel label={constantFormValues?.endOfFinancialYear?.label} head={true} />
// 					</Col>
// 					<Col xs={8}>
// 						<Form.Item
// 							name={constantFormValues?.endOfFinancialYear?.key}
// 							initialValue={dayjs().month(11).date(31)}
// 							rules={[
// 								{
// 									required: false,
// 								},
// 							]}
// 						>
// 							<DatePicker
// 								disabled
// 								picker="date"
// 								format="DD MMMM"
// 								allowClear={false}
// 								disabledDate={() => false}
// style={{width: '100%'}}
// 								onChange={(date) => {
// 									if (date) {
// 										const currentYear = dayjs().year();
// 										const newDate = date.year(currentYear);
// 										form.setFieldsValue({
// 											[constantFormValues?.endOfFinancialYear?.key]: newDate,
// 										});
// 									}
// 								}}
// 							/>
// 						</Form.Item>
// 					</Col>
// 				</Row>
// 			</CardAccordion>

// 			<CardAccordion
// 				title="Accounting Information"
// 				customHeaderSX={{
// 					backgroundColor: '#e1f0ff !important',
// 					p: '10px',
// 				}}
// 				typographySX={{ fontSize: '16px !important' }}
// 				darkTitle={true}
// 				sx={{ mb: 2 }}
// 			>
// 				<Row gutter={[16, 0]}>
// 					{Object.entries(glCodesShortForShow).map(([label, key], index) => (
// 						<Fragment key={index}>
// 							<Col xs={4}>
// 								<InputLabel label={label} head={true} />
// 							</Col>
// 							<Col xs={8}>
// 								<Form.Item name={key}>
// 									<Select
// 										allowClear
// 										showSearch
// 										autoComplete="off"
// 										placeholder="Select from the list"
// 										styles={{
// 											popup: {
// 												root: { zIndex: 1301 },
// 											},
// 										}}
// 										filterOption={filterOptionLabel}
// 										options={glOptions}
// 										loading={isGeneralLedgerListLoading}
// 										disabled
// 									/>
// 								</Form.Item>
// 							</Col>
// 						</Fragment>
// 					))}
// 				</Row>
// 			</CardAccordion>

// 			<CardAccordion
// 				title="Accounting Information"
// 				customHeaderSX={{
// 					backgroundColor: '#e1f0ff !important',
// 					p: '10px',
// 				}}
// 				typographySX={{ fontSize: '16px !important' }}
// 				darkTitle={true}
// 				sx={{ mb: 2, display: 'none' }}
// 			>
// 				<Row gutter={[16, 0]}>
// 					{Object.entries(glCodesToShow).map(([label, key], index) => (
// 						<Fragment key={index}>
// 							<Col xs={4}>
// 								<InputLabel label={label} head={true} />
// 							</Col>
// 							<Col xs={8}>
// 								<Form.Item name={key}>
// 									<Select
// 										allowClear
// 										showSearch
// 										placeholder="Select from the list"
// 										autoComplete="off"
// 										styles={{
// 											popup: {
// 												root: { zIndex: 1301 },
// 											},
// 										}}
// 										filterOption={filterOptionLabel}
// 										options={glOptions}
// 										loading={isGeneralLedgerListLoading}
// 										disabled
// 									/>
// 								</Form.Item>
// 							</Col>
// 						</Fragment>
// 					))}
// 				</Row>
// 			</CardAccordion>
// 		</>
// 	);
// };

// export default LeaseRecordManagementV9AccountAssignmentDetail;
